/* ============================================================================
   Mock data — the user's garden: plants, care schedule and AI scans.
   Includes a lost plant on purpose: a demo that only shows success hides the
   states the product actually has to handle.
   ========================================================================== */

import type { CareTask, DiseaseReport, Plant, Species } from '@core/types'
import { SPECIES } from './species'
import { isoFromNow, isoMonthsAgo } from './climate'

function species(id: string): Species {
  const found = SPECIES.find((item) => item.id === id)
  if (!found) throw new Error(`Mock data error: unknown species "${id}"`)
  return found
}

export const PLANTS: Plant[] = [
  {
    id: 'pl-0001',
    nickname: 'Nakhla — date palm behind the house',
    species: species('sp-date-palm'),
    plantedOn: isoMonthsAgo(29, 12),
    location: { governorate: 'Baghdad', district: 'Abu Ghraib', latitude: 33.3, longitude: 44.06 },
    health: 'thriving',
    ageMonths: 29,
    lastCheckInAt: isoFromNow(-6, 17),
    nextCareDueAt: isoFromNow(2, 7),
    pointsEarned: 640,
    photoLabel: 'date-palm-abu-ghraib-2026-08-19.jpg',
  },
  {
    id: 'pl-0002',
    nickname: 'Sidr by the school wall',
    species: species('sp-sidr'),
    plantedOn: isoMonthsAgo(14, 3),
    location: { governorate: 'Baghdad', district: 'Karrada', latitude: 33.29, longitude: 44.42 },
    health: 'needs-water',
    ageMonths: 14,
    lastCheckInAt: isoFromNow(-21, 18),
    nextCareDueAt: isoFromNow(-2, 7),
    pointsEarned: 310,
    photoLabel: 'sidr-karrada-2026-09-04.jpg',
    notes: 'Leaves dull and dusty after the September dust storms. Soil dry to about 20 cm.',
  },
  {
    id: 'pl-0003',
    nickname: 'Bougainvillea at the main gate',
    species: species('sp-bougainvillea'),
    plantedOn: isoMonthsAgo(20, 21),
    location: { governorate: 'Basra', district: 'Al-Zubair', latitude: 30.39, longitude: 47.7 },
    health: 'thriving',
    ageMonths: 20,
    lastCheckInAt: isoFromNow(-9, 8),
    nextCareDueAt: isoFromNow(12, 7),
    pointsEarned: 420,
    photoLabel: 'bougainvillea-zubair-2026-09-16.jpg',
  },
  {
    id: 'pl-0004',
    nickname: 'Lemon tree in the courtyard',
    species: species('sp-lemon'),
    plantedOn: isoMonthsAgo(9, 8),
    location: { governorate: 'Erbil', district: 'Ainkawa', latitude: 36.24, longitude: 43.99 },
    health: 'at-risk',
    ageMonths: 9,
    lastCheckInAt: isoFromNow(-3, 19),
    nextCareDueAt: isoFromNow(-1, 7),
    pointsEarned: 150,
    photoLabel: 'lemon-ainkawa-2026-09-22.jpg',
    notes:
      'Lower leaves yellowing since mid-July. The irrigation line is shared with the neighbour and pressure drops every afternoon.',
  },
  {
    id: 'pl-0005',
    nickname: 'Oleander hedge — 14 plants along the street',
    species: species('sp-oleander'),
    plantedOn: isoMonthsAgo(33, 5),
    location: { governorate: 'Najaf', district: 'Kufa', latitude: 32.03, longitude: 44.4 },
    health: 'thriving',
    ageMonths: 33,
    lastCheckInAt: isoFromNow(-12, 16),
    nextCareDueAt: isoFromNow(4, 7),
    pointsEarned: 520,
    photoLabel: 'oleander-hedge-kufa-2026-09-13.jpg',
  },
  {
    id: 'pl-0006',
    nickname: 'Athel windbreak row (12 trees)',
    species: species('sp-athel'),
    plantedOn: isoMonthsAgo(26, 17),
    location: { governorate: 'Muthanna', district: 'Al-Samawah', latitude: 31.31, longitude: 45.28 },
    health: 'lost',
    ageMonths: 26,
    lastCheckInAt: isoFromNow(-48, 10),
    nextCareDueAt: null,
    pointsEarned: 180,
    photoLabel: 'athel-windbreak-samawah-2026-08-08.jpg',
    notes:
      'Lost after the pump failure in July 2026 — the whole row dried out within three weeks. Two Sidr seedlings are planned for the November planting window.',
  },
]

export const CARE_TASKS: CareTask[] = [
  {
    id: 'ct-2001',
    plantId: 'pl-0002',
    plantName: 'Sidr by the school wall',
    type: 'water',
    title: 'Deep water the Sidr',
    instructions:
      '40 litres in a slow basin around the trunk so it soaks in instead of running off. Avoid wetting the leaves.',
    dueAt: isoFromNow(-2, 7),
    status: 'overdue',
    repeatEveryDays: 12,
    rewardPoints: 20,
  },
  {
    id: 'ct-2002',
    plantId: 'pl-0004',
    plantName: 'Lemon tree in the courtyard',
    type: 'inspect',
    title: 'Inspect yellowing lemon leaves',
    instructions:
      'Photograph two affected leaves, check soil moisture at 15 cm depth and note whether the drip line is still delivering water.',
    dueAt: isoFromNow(-1, 7),
    status: 'overdue',
    repeatEveryDays: 7,
    rewardPoints: 25,
  },
  {
    id: 'ct-2003',
    plantId: 'pl-0001',
    plantName: 'Nakhla — date palm behind the house',
    type: 'water',
    title: 'Irrigate the date palm',
    instructions:
      '80 litres around the trunk, early morning or after sunset to reduce evaporation losses.',
    dueAt: isoFromNow(0, 7),
    status: 'due',
    repeatEveryDays: 7,
    rewardPoints: 20,
  },
  {
    id: 'ct-2004',
    plantId: 'pl-0001',
    plantName: 'Nakhla — date palm behind the house',
    type: 'photo',
    title: 'Monthly verification photo',
    instructions:
      'One photo of the whole tree with the trunk base visible. This is what keeps the survival record accurate.',
    dueAt: isoFromNow(0, 18),
    status: 'due',
    repeatEveryDays: 30,
    rewardPoints: 30,
  },
  {
    id: 'ct-2005',
    plantId: 'pl-0004',
    plantName: 'Lemon tree in the courtyard',
    type: 'fertilise',
    title: 'Citrus feed — second application',
    instructions:
      '60 g of citrus fertiliser spread 30 cm from the trunk, then watered in thoroughly.',
    dueAt: isoFromNow(2, 8),
    status: 'upcoming',
    repeatEveryDays: 90,
    rewardPoints: 25,
  },
  {
    id: 'ct-2006',
    plantId: 'pl-0005',
    plantName: 'Oleander hedge — 14 plants along the street',
    type: 'water',
    title: 'Water the oleander hedge',
    instructions: '20 litres per plant. Skip any plant whose soil is still damp below the surface.',
    dueAt: isoFromNow(4, 7),
    status: 'upcoming',
    repeatEveryDays: 10,
    rewardPoints: 20,
  },
  {
    id: 'ct-2007',
    plantId: 'pl-0005',
    plantName: 'Oleander hedge — 14 plants along the street',
    type: 'prune',
    title: 'Cut the oldest third of oleander stems',
    instructions:
      'Remove the oldest third of stems at ground level so the hedge flowers next spring. Wear gloves — oleander sap irritates skin.',
    dueAt: isoFromNow(9, 8),
    status: 'upcoming',
    repeatEveryDays: null,
    rewardPoints: 35,
  },
  {
    id: 'ct-2008',
    plantId: 'pl-0003',
    plantName: 'Bougainvillea at the main gate',
    type: 'mulch',
    title: 'Mulch the bougainvillea root zone',
    instructions:
      '5 cm of composted mulch kept 10 cm clear of the stem. This cuts summer watering by roughly a third in Basra heat.',
    dueAt: isoFromNow(12, 8),
    status: 'upcoming',
    repeatEveryDays: null,
    rewardPoints: 30,
  },
  {
    id: 'ct-2009',
    plantId: 'pl-0006',
    plantName: 'Athel windbreak row (12 trees)',
    type: 'inspect',
    title: 'Confirm loss and plan replacement',
    instructions:
      'Marked as lost after the pump failure. Schedule replacement seedlings for the November planting window.',
    dueAt: isoFromNow(-1, 9),
    status: 'completed',
    repeatEveryDays: null,
    rewardPoints: 15,
  },
]

export const DISEASE_REPORTS: DiseaseReport[] = [
  {
    id: 'dr-3001',
    plantId: 'pl-0001',
    plantName: 'Nakhla — date palm behind the house',
    capturedAt: isoFromNow(-30, 11),
    status: 'complete',
    modelVersion: 'mobilenetv3-iq-0.4',
    imageLabel: 'palm-leaf-detail-2026-08-26.jpg',
    diagnosis: {
      label: 'Graphiola leaf spot (false smut)',
      labelAr: 'تبقع أوراق النخيل',
      confidence: 0.87,
      severity: 'moderate',
      description:
        'Small black pustules arranged in rings on the older fronds, typical after a humid spell followed by heat. Yield is rarely affected but the fronds lose vigour.',
      treatmentSteps: [
        'Cut and burn the three most affected fronds, sterilising the blade between cuts.',
        'Stop overhead watering — water the basin only, early morning.',
        'Apply a copper-based fungicide to the crown every 14 days, three times.',
        'Re-photograph the same frond position in 30 days so the change is comparable.',
      ],
      nurseryAdvice:
        'Copper fungicide is stocked by most Baghdad nurseries. Two litres of concentrate covers three applications.',
    },
  },
  {
    id: 'dr-3002',
    plantId: 'pl-0003',
    plantName: 'Bougainvillea at the main gate',
    capturedAt: isoFromNow(-9, 16),
    status: 'complete',
    modelVersion: 'mobilenetv3-iq-0.4',
    imageLabel: 'bougainvillea-leaves-2026-09-16.jpg',
    diagnosis: {
      label: 'Powdery mildew',
      labelAr: 'البياض الدقيقي',
      confidence: 0.74,
      severity: 'low',
      description:
        'White powdery coating on new growth, encouraged by warm days, cool nights and poor air movement against the wall.',
      treatmentSteps: [
        'Remove the affected tips and dispose of them away from the garden.',
        'Thin the canopy so air moves through the wall side.',
        'Spray a potassium-bicarbonate solution weekly for three weeks.',
      ],
      nurseryAdvice:
        'No chemical purchase is needed yet — try the cultural steps for three weeks and re-scan.',
    },
  },
  {
    id: 'dr-3003',
    plantId: 'pl-0004',
    plantName: 'Lemon tree in the courtyard',
    capturedAt: isoFromNow(-3, 19),
    status: 'failed',
    failureReason:
      'The photo was too dark and out of focus, so the model could not separate the leaf from the background. Retake it in daylight, filling most of the frame with one leaf.',
    modelVersion: 'mobilenetv3-iq-0.4',
    imageLabel: 'lemon-leaves-evening-2026-09-22.jpg',
  },
]

